/* ==========================================================================
   SOLUTIONS DATA - copy for the six solution pillars + /solutions index.
   Each entry drives /solutions/<slug> via SolutionPage.js.
   `icon` values map to lucide-react names in SolutionPage.js.

   Positioning: Versa Launch is a business technology company. Solutions are
   capability pillars, not one-off services. Industries pages show how the
   pillars combine for a specific kind of business.

   Brand: white / black / gold. No em-dashes anywhere.
   ========================================================================== */

export const SOLUTIONS = [
  {
    slug: 'websites-commerce',
    name: 'Website & Commerce',
    icon: 'Globe',
    blurb: 'The site customers judge you by, plus the storefront that takes the order.',
    seoTitle: 'Website & Commerce - Versa Launch',
    seoDescription:
      'Custom websites and online storefronts built, hosted, and managed by Versa Launch. Fast, mobile-first, built to convert, and connected to the rest of your business technology.',
    hero: {
      eyebrow: 'Website & Commerce',
      lead: 'The front door to your',
      accent: 'business',
      sub: 'A custom site that loads fast, reads clean on a phone, and turns visitors into calls, bookings, and orders. Built by us, hosted by us, updated by us, and wired into your phones, your search presence, and your back office.'
    },
    painsTitle: 'Your website is doing less than you think',
    painsSub:
      'Most small business sites were built once and never touched again. They look dated, load slowly, and quietly hand customers to whoever ranks above you.',
    pains: [
      {
        title: 'It looks fine on your laptop, rough on a phone',
        text: 'Most of your traffic is mobile. If the text is small, the buttons are cramped, and the page takes five seconds to load, people leave before they ever see what you do.'
      },
      {
        title: 'Nobody can change anything without calling someone',
        text: 'New hours, a new service, a price change. If every edit means an email to a developer and a two week wait, the site slowly drifts out of date until it works against you.'
      },
      {
        title: 'Traffic comes in and nothing happens',
        text: 'Visitors show up, read a paragraph, and leave. Without clear next steps, lead capture, and a checkout that works, a site is a brochure that costs money.'
      }
    ],
    whatTitle: 'What we build',
    whatSub:
      'One website that carries the brand, ranks locally, sells online when you need it to, and connects to everything else we run for you.',
    capabilities: [
      {
        icon: 'Sparkles',
        title: 'Custom design, not a template',
        text: 'Every page is designed around your business, your photos, and the way your customers actually buy. No bloated page builders, no theme that looks like ten other companies in your zip code.'
      },
      {
        icon: 'Smartphone',
        title: 'Mobile-first and fast',
        text: 'Built for the phone first, because that is where your customers are. Compressed images, clean code, and hosting tuned so pages open before people lose patience.'
      },
      {
        icon: 'ShoppingCart',
        title: 'Ecommerce and online ordering',
        text: 'Sell products, take pickup and delivery orders, or collect deposits. Connected to Stripe, Square, and Clover so payments land in the systems you already use.'
      },
      {
        icon: 'ClipboardCheck',
        title: 'Lead capture that routes',
        text: 'Quote forms, intake forms, and booking flows that send the lead where it needs to go. Text alert to your phone, email to the office, and a record in your dashboard.'
      },
      {
        icon: 'Search',
        title: 'Search foundations built in',
        text: 'Clean page structure, real metadata, schema markup, and speed scores that give you a fighting chance in local search from the day you go live.'
      },
      {
        icon: 'Repeat',
        title: 'Changes without the wait',
        text: 'Send us the update and it goes live. Seasonal hours, a new service page, a price change. Managed edits are part of what you already pay for.'
      }
    ],
    includedTitle: 'Included with every website engagement',
    included: [
      'Custom design, copy cleanup, and brand polish',
      'Managed hosting, SSL, backups, and uptime monitoring',
      'Mobile, speed, and accessibility passes before launch',
      'Migration from WordPress, Wix, Squarespace, or Shopify',
      'Analytics and conversion tracking wired up correctly',
      'Ongoing edits and updates handled by our team'
    ],
    related: [
      { label: 'Custom design detail', to: '/services/custom-design' },
      { label: 'Square integrations', to: '/services/square-integrations' },
      { label: 'Search & Growth', to: '/solutions/search-growth' }
    ],
    cta: {
      title: 'See what your site could look like',
      text: 'We will build you a free demo of your new homepage before you commit to anything. Look at it, keep it or leave it, no pressure either way.'
    }
  },

  {
    slug: 'communications',
    name: 'Communications',
    icon: 'Phone',
    blurb: 'A real business phone system, texting, and routing that gets calls to the right person.',
    seoTitle: 'Business Communications & Phone Systems - Versa Launch',
    seoDescription:
      'Business phone lines, smart call routing, business texting, and voicemail handled by Versa Launch. A professional communication system for local businesses, fully managed.',
    hero: {
      eyebrow: 'Communications',
      lead: 'Every call reaches the right',
      accent: 'person',
      sub: 'A dedicated business line, routing rules that follow your schedule, business texting customers actually reply to, and voicemail that turns into a task instead of a dead end. Set up and managed for you.'
    },
    painsTitle: 'The phone is still how most jobs get won',
    painsSub:
      'For local businesses, the call is the sale. Most communication problems are not about volume, they are about calls landing nowhere.',
    pains: [
      {
        title: 'Business is running through a personal cell',
        text: 'Your number is on a truck, a sign, and 200 business cards. It is also the number your kids call. There is no way to hand it off, no record of anything, and no way to step away.'
      },
      {
        title: 'Calls ring once and disappear',
        text: 'One person is on another line, one is on a job, and the call rolls to a voicemail nobody checks until Thursday. The customer already called the next name on the list.'
      },
      {
        title: 'Customers text and nobody sees it',
        text: 'People would rather text than call, but a landline cannot receive one and a personal phone buries it. Quotes, confirmations, and reschedules get lost in a thread nobody owns.'
      }
    ],
    whatTitle: 'What we set up',
    whatSub:
      'A communication system that behaves like a real company runs it, without a phone closet, a contract, or an IT vendor.',
    capabilities: [
      {
        icon: 'Phone',
        title: 'A real business line',
        text: 'A dedicated business number that works from any phone. Keep your personal number private, hand the business line to whoever is on duty, and keep it when your team changes.'
      },
      {
        icon: 'Users',
        title: 'Routing and ring groups',
        text: 'Ring the office first, then the field, then the owner. Route by time of day, by service, or by who is actually working. Nothing bounces to a dead extension.'
      },
      {
        icon: 'MessageSquare',
        title: 'Business texting',
        text: 'Send and receive texts on your business number. Confirmations, quotes, on-my-way messages, and follow up, all on the number customers already have.'
      },
      {
        icon: 'Voicemail',
        title: 'Voicemail that becomes a task',
        text: 'Voicemails get transcribed and pushed to you as text and email, so a message left at 9pm is a callback on your list at 7am instead of a surprise on Friday.'
      },
      {
        icon: 'Clock',
        title: 'Hours, holidays, and after-hours',
        text: 'Different greeting and routing for nights, weekends, and holidays. Set it once and the system follows your calendar without anyone remembering to flip a switch.'
      },
      {
        icon: 'BarChart3',
        title: 'Call records you can act on',
        text: 'See who called, when, how long it lasted, and what was missed. Patterns in the log tell you when to staff up and which marketing is actually producing calls.'
      }
    ],
    includedTitle: 'Included with communications',
    included: [
      'Number provisioning or porting your existing number',
      'Routing, ring groups, and after-hours rules configured for you',
      'Business texting on your main line',
      'Voicemail transcription to text and email',
      'Call logs and reporting in your dashboard',
      'Changes to routing and greetings handled by our team'
    ],
    related: [
      { label: 'Business phone detail', to: '/services/business-phone' },
      { label: 'AI Reception', to: '/solutions/ai-reception' },
      { label: 'Business Software', to: '/solutions/business-software' }
    ],
    cta: {
      title: 'Stop losing calls you already paid to get',
      text: 'We will map your current call flow, show you where calls are dropping, and set up a system that catches them. Takes about fifteen minutes to walk through.'
    }
  },

  {
    slug: 'ai-reception',
    name: 'AI Reception',
    icon: 'Bot',
    blurb: 'An assistant that answers every call, books the job, and never takes a day off.',
    seoTitle: 'AI Reception & 24/7 Call Answering - Versa Launch',
    seoDescription:
      'An AI receptionist that answers every call 24/7, answers customer questions, captures lead details, and books appointments. Built, trained, and managed by Versa Launch.',
    hero: {
      eyebrow: 'AI Reception',
      lead: 'Every call answered, every',
      accent: 'time',
      sub: 'An AI receptionist trained on your business that picks up on the first ring, answers real questions, takes the details, books the appointment, and hands you a clean summary. Nights, weekends, and the middle of a job included.'
    },
    painsTitle: 'The calls you miss are the ones that cost the most',
    painsSub:
      'Most local businesses answer well under half the calls that come in. The customer does not leave a message. They call the next result.',
    pains: [
      {
        title: 'You cannot answer and work at the same time',
        text: 'You are under a sink, on a ladder, or with a client. The phone rings and you either lose the call or lose your focus. Both cost money.'
      },
      {
        title: 'After hours is when people actually call',
        text: 'The pipe breaks at 10pm and the search happens right then. If nobody picks up, somebody else does, and that customer is theirs from then on.'
      },
      {
        title: 'An answering service reads a script',
        text: 'Generic call centers take a name and a number and know nothing about your services, your pricing, or your calendar. Customers can tell, and the message still lands back on your plate.'
      }
    ],
    whatTitle: 'What the assistant does',
    whatSub:
      'Not a phone tree and not a call center. A trained assistant that knows your business and can finish the conversation.',
    capabilities: [
      {
        icon: 'PhoneCall',
        title: 'Answers on the first ring, 24/7',
        text: 'No hold music, no voicemail, no after-hours gap. Every caller gets a real conversation whether it is 2pm Tuesday or 11pm Saturday.'
      },
      {
        icon: 'MessageSquare',
        title: 'Trained on your actual business',
        text: 'Hours, service area, what you do and do not take on, pricing ranges, parking, policies. It answers the questions your front desk would answer, in your words.'
      },
      {
        icon: 'CalendarCheck',
        title: 'Books directly into your calendar',
        text: 'It checks live availability, offers real slots, books the appointment, and sends a confirmation text. You see the job on your calendar, not a note to call someone back.'
      },
      {
        icon: 'ClipboardCheck',
        title: 'Captures the lead properly',
        text: 'Name, number, address, the job, and the urgency, collected every time and pushed to you the moment the call ends. Nothing depends on someone remembering to write it down.'
      },
      {
        icon: 'UserPlus',
        title: 'Knows when to hand off',
        text: 'Emergencies, big jobs, and anything outside its lane get escalated to a human immediately with the context already gathered. It does not stall a caller who needs you.'
      },
      {
        icon: 'FileText',
        title: 'Full transcript and summary',
        text: 'Every call comes back as a recording, a transcript, and a short summary. Review what customers are asking and use it to sharpen pricing, staffing, and your site.'
      }
    ],
    includedTitle: 'Included with AI reception',
    included: [
      'Assistant trained on your services, pricing, and policies',
      'Calendar and booking integration',
      'Instant text and email handoff of every lead',
      'Escalation rules for emergencies and priority callers',
      'Recordings, transcripts, and call summaries',
      'Ongoing tuning as your business and offers change'
    ],
    related: [
      { label: 'AI phone assistant detail', to: '/services/ai-phone-assistant' },
      { label: 'Communications', to: '/solutions/communications' },
      { label: 'Business Software', to: '/solutions/business-software' }
    ],
    cta: {
      title: 'Hear it answer your phone',
      text: 'We will set up a live demo trained on your business so you can call in and hear exactly how it handles a real customer. No commitment to listen.'
    }
  },

  {
    slug: 'search-growth',
    name: 'Search & Growth',
    icon: 'TrendingUp',
    blurb: 'Get found on Google, get recommended by AI, and turn reviews into a moat.',
    seoTitle: 'Local Search, SEO, AEO & Growth - Versa Launch',
    seoDescription:
      'Local SEO, Google Business Profile management, answer engine optimization, and review generation from Versa Launch. Get found by customers searching and by the AI tools they now ask.',
    hero: {
      eyebrow: 'Search & Growth',
      lead: 'Get found before your',
      accent: 'competitor',
      sub: 'Local search, map pack rankings, answer engine optimization, and a steady flow of new reviews. We handle the work that decides whether a customer ever sees you at all.'
    },
    painsTitle: 'Being good is not the same as being findable',
    painsSub:
      'Customers do not compare every option. They pick from what appears first, and increasingly from a single answer an AI hands them.',
    pains: [
      {
        title: 'You are on page two for what you actually sell',
        text: 'The top few local results take the overwhelming majority of clicks. If you are below them, most customers never learn you exist, no matter how good the work is.'
      },
      {
        title: 'AI is answering the question instead of listing links',
        text: 'People ask ChatGPT and Google AI Overviews for a recommendation and take the answer. If your business data is not structured for machines to read, you are not in that answer.'
      },
      {
        title: 'Your reviews stalled two years ago',
        text: 'A competitor with 180 recent reviews outranks and out-converts a business with 30 old ones, every time. Nobody on your team has time to ask, so nobody asks.'
      }
    ],
    whatTitle: 'What we run for you',
    whatSub:
      'Ongoing work, not a one-time audit. Search moves constantly and so does the work behind your ranking.',
    capabilities: [
      {
        icon: 'MapPin',
        title: 'Local search and the map pack',
        text: 'Google Business Profile optimization, category and service tuning, photos, posts, citations, and service area targeting so you show up where your customers are actually looking.'
      },
      {
        icon: 'Search',
        title: 'Technical and on-page SEO',
        text: 'Site speed, mobile experience, crawlability, page structure, and schema markup handled continuously so Google understands what you do and where you do it.'
      },
      {
        icon: 'Bot',
        title: 'Answer engine optimization',
        text: 'Your services, pricing, areas, and FAQs published in the machine-readable formats AI assistants pull from, with a consistent business entity across the web so models trust it.'
      },
      {
        icon: 'Star',
        title: 'Review generation and response',
        text: 'Automatic review requests to happy customers after the job, routed to Google where it counts, plus help responding to the ones that need a reply.'
      },
      {
        icon: 'FileText',
        title: 'Content that answers real questions',
        text: 'Service pages, location pages, and question-and-answer content written the way customers actually ask, which is what both Google and AI assistants reward.'
      },
      {
        icon: 'BarChart3',
        title: 'Rank tracking and reporting',
        text: 'Keyword positions, map pack placement, calls, and form fills in one dashboard. You see what moved, what it produced, and what we are working on next.'
      }
    ],
    includedTitle: 'Included with search & growth',
    included: [
      'Google Business Profile setup, cleanup, and ongoing management',
      'Technical SEO monitoring and fixes',
      'Schema and AEO structured data maintained as standards change',
      'Local citation building and consistency checks',
      'Automated review requests and response support',
      'Monthly rank, traffic, and lead reporting'
    ],
    related: [
      { label: 'SEO guide', to: '/resources/seo-guide' },
      { label: 'Free visibility report', to: '/competition-report' },
      { label: 'Website & Commerce', to: '/solutions/websites-commerce' }
    ],
    cta: {
      title: 'See where you actually rank right now',
      text: 'Run a free visibility report and we will show you your position against the competitors in your area, plus exactly what is holding you back.'
    }
  },

  {
    slug: 'business-software',
    name: 'Business Software',
    icon: 'Code2',
    blurb: 'Portals, forms, automations, and custom tools built around how you really work.',
    seoTitle: 'Custom Business Software & Automation - Versa Launch',
    seoDescription:
      'Client portals, intake forms, loyalty programs, integrations, and custom web apps built by Versa Launch to automate the operations behind a growing local business.',
    hero: {
      eyebrow: 'Business Software',
      lead: 'Software built around your',
      accent: 'operation',
      sub: 'Client portals, intake forms, scheduling, payments, loyalty, and the automations that connect them. When off-the-shelf tools do not fit the way you work, we build the piece that does.'
    },
    painsTitle: 'The work between the work is eating your week',
    painsSub:
      'Most local businesses lose more hours to admin than to the actual job. Usually to a stack of tools that do not talk to each other.',
    pains: [
      {
        title: 'Everything lives in a different place',
        text: 'Quotes in email, schedule in a calendar, payments in Square, customers in a notebook. Nothing syncs, so somebody retypes the same information four times a day.'
      },
      {
        title: 'You are paying for software that half fits',
        text: 'Four subscriptions, each doing sixty percent of what you need, none of them built for your business. The gaps get filled by a person doing manual work forever.'
      },
      {
        title: 'Follow-up depends on somebody remembering',
        text: 'Reminders, deposits, review requests, and repeat outreach only happen when the team has a slow moment. Which is never during your busy season.'
      }
    ],
    whatTitle: 'What we build and connect',
    whatSub:
      'Start with the automations that pay for themselves, then build custom where it actually matters.',
    capabilities: [
      {
        icon: 'Lock',
        title: 'Client portals',
        text: 'A branded place for customers to book, upload documents, approve quotes, pay, and see their history. Less back and forth for them, fewer interruptions for you.'
      },
      {
        icon: 'ClipboardCheck',
        title: 'Intake forms and quoting',
        text: 'Forms that ask the right questions the first time, route by service type, and arrive with everything you need to price the job without a follow-up call.'
      },
      {
        icon: 'Zap',
        title: 'Automations and integrations',
        text: 'Square, Stripe, Clover, calendars, email, and your CRM wired together so a booking creates a customer, an invoice, a reminder, and a review request without anyone touching it.'
      },
      {
        icon: 'Gift',
        title: 'Loyalty and referrals',
        text: 'Rewards and referral programs that run themselves, bringing back the customers you already earned instead of buying new ones every month.'
      },
      {
        icon: 'Code2',
        title: 'Custom web apps',
        text: 'When your operation genuinely does not fit existing software, we build the tool. Inventory, dispatch, quoting, member management, whatever the actual bottleneck is.'
      },
      {
        icon: 'BarChart3',
        title: 'One dashboard over all of it',
        text: 'Calls, leads, bookings, payments, reviews, and rankings in a single view, so you can tell what is working without opening six tabs.'
      }
    ],
    includedTitle: 'Included with business software',
    included: [
      'Discovery session mapping your current workflow',
      'Portal, form, and automation build and configuration',
      'Integration with the payment and calendar tools you already use',
      'Staff walkthrough and documentation',
      'Hosting, monitoring, and security handled by us',
      'Changes and new automations as the business evolves'
    ],
    related: [
      { label: 'Client portals detail', to: '/services/client-portals' },
      { label: 'Web apps detail', to: '/services/web-apps' },
      { label: 'Rewards program', to: '/services/rewards' }
    ],
    cta: {
      title: 'Tell us what is eating your time',
      text: 'Walk us through a week. We will point at the parts a system should be doing and tell you honestly which ones are worth automating first.'
    }
  },

  {
    slug: 'support',
    name: 'IT & Support',
    icon: 'LifeBuoy',
    blurb: 'One number for the technology behind your business, answered by people who built it.',
    seoTitle: 'Managed Technology Support - Versa Launch',
    seoDescription:
      'Ongoing management, monitoring, security, and human support for the technology your business runs on. One team, one number, no ticket queue.',
    hero: {
      eyebrow: 'IT & Support',
      lead: 'One team behind all of',
      accent: 'it',
      sub: 'Hosting, updates, security, monitoring, and a real person to call when something breaks. We manage what we build, so nothing lands in the gap between three different vendors.'
    },
    painsTitle: 'Nobody owns the technology, so everybody owns the problem',
    painsSub:
      'The usual setup is a web guy who stopped answering, a phone provider with a ticket queue, and a nephew who set up the email. When something breaks, you are the integration layer.',
    pains: [
      {
        title: 'Every vendor points at another vendor',
        text: 'The site is down, the host blames the domain, the domain blames the developer, and you spend a morning on hold to learn nothing. Meanwhile customers see an error page.'
      },
      {
        title: 'Support means a ticket and a wait',
        text: 'You submit a form, get an auto-reply, and hear back in two business days from someone who has never seen your setup. That is not support, that is a queue.'
      },
      {
        title: 'Nobody is watching until a customer tells you',
        text: 'An expired certificate, a full inbox, a form that silently stopped sending. Problems like these run for weeks because nothing is monitoring them and no one is looking.'
      }
    ],
    whatTitle: 'What we handle',
    whatSub:
      'The unglamorous, continuous work that keeps a business running. Included, not billed hourly.',
    capabilities: [
      {
        icon: 'Globe',
        title: 'Hosting, domains, and email',
        text: 'Managed hosting, SSL certificates, domain and DNS records, and business email records kept current. Renewals do not lapse because they are on our calendar, not yours.'
      },
      {
        icon: 'Bell',
        title: 'Monitoring and uptime',
        text: 'Uptime, page speed, form delivery, and certificate expiration watched around the clock. In most cases we know and have fixed it before you would have noticed.'
      },
      {
        icon: 'Wrench',
        title: 'Updates and maintenance',
        text: 'Software updates, backups, broken link cleanup, and content edits. Send it over and it gets done, no change order and no hourly meter running.'
      },
      {
        icon: 'CheckCircle',
        title: 'Security and backups',
        text: 'Hardened configuration, regular backups, and a tested restore path. If something goes wrong we roll back, rather than rebuilding from whatever anyone happens to have saved.'
      },
      {
        icon: 'Phone',
        title: 'A real person, one number',
        text: 'Call or text the team that built your setup. No tier one script, no repeating your history, no waiting on a queue to reach someone who understands the system.'
      },
      {
        icon: 'Users',
        title: 'Onboarding and training',
        text: 'We set your staff up, walk them through the tools, and stay available when you hire. New employees get trained without pulling you off the floor.'
      }
    ],
    includedTitle: 'Included with support',
    included: [
      'Managed hosting, SSL, DNS, and domain renewals',
      '24/7 uptime, speed, and form delivery monitoring',
      'Regular backups with a tested restore path',
      'Ongoing content edits and software updates',
      'Direct phone, text, and email access to our team',
      'Staff onboarding and training as your team changes'
    ],
    related: [
      { label: 'Pricing', to: '/plans' },
      { label: 'Getting started guide', to: '/resources/getting-started' },
      { label: 'Book a call', to: '/book' }
    ],
    cta: {
      title: 'Put one team behind your technology',
      text: 'Tell us what you are running today and who supports it. We will show you what moving it under one roof looks like, including what it costs.'
    }
  }
];

export const SOLUTION_SLUGS = SOLUTIONS.map((s) => s.slug);

export function getSolution(slug) {
  return SOLUTIONS.find((s) => s.slug === slug) || null;
}
